/**
 * A* Pathfinding implementation for unit movement
 * Uses tile-based navigation with obstacle avoidance
 */

import { tileHeight, isLand } from '@/rendering/terrain/TerrainGenerator';
import { tileOccupancy } from '@/game/systems/TileOccupancy';
import { obstacleRegistry } from '@/game/systems/ObstacleRegistry';

export interface PathNode {
  x: number;
  z: number;
  g: number; // Cost from start
  h: number; // Heuristic to goal
  f: number; // Total cost (g + h)
  parent: PathNode | null;
}

export interface Waypoint {
  x: number;
  z: number;
}

// Priority queue for A* open set
class MinHeap {
  private heap: PathNode[] = [];

  push(node: PathNode): void {
    this.heap.push(node);
    this.bubbleUp(this.heap.length - 1);
  }

  pop(): PathNode | undefined {
    if (this.heap.length === 0) return undefined;
    const result = this.heap[0];
    const last = this.heap.pop()!;
    if (this.heap.length > 0) {
      this.heap[0] = last;
      this.bubbleDown(0);
    }
    return result;
  }

  isEmpty(): boolean {
    return this.heap.length === 0;
  }

  private bubbleUp(index: number): void {
    while (index > 0) {
      const parent = Math.floor((index - 1) / 2);
      if (this.heap[parent].f <= this.heap[index].f) break;
      [this.heap[parent], this.heap[index]] = [this.heap[index], this.heap[parent]];
      index = parent;
    }
  }

  private bubbleDown(index: number): void {
    const length = this.heap.length;
    while (true) {
      const left = 2 * index + 1;
      const right = 2 * index + 2;
      let smallest = index;

      if (left < length && this.heap[left].f < this.heap[smallest].f) {
        smallest = left;
      }
      if (right < length && this.heap[right].f < this.heap[smallest].f) {
        smallest = right;
      }
      if (smallest === index) break;

      [this.heap[smallest], this.heap[index]] = [this.heap[index], this.heap[smallest]];
      index = smallest;
    }
  }
}

// Direction offsets for 8-directional movement
const DIRECTIONS = [
  { dx: 0, dz: -1, cost: 1.0 },   // North
  { dx: 1, dz: -1, cost: 1.414 }, // NE
  { dx: 1, dz: 0, cost: 1.0 },    // East
  { dx: 1, dz: 1, cost: 1.414 },  // SE
  { dx: 0, dz: 1, cost: 1.0 },    // South
  { dx: -1, dz: 1, cost: 1.414 }, // SW
  { dx: -1, dz: 0, cost: 1.0 },   // West
  { dx: -1, dz: -1, cost: 1.414 }, // NW
];

// Max iterations to prevent infinite loops
const MAX_ITERATIONS = 2000;

// Cache for walkability checks
const walkabilityCache = new Map<string, boolean>();
let walkabilityCacheVersion = 0;

/**
 * Clear walkability cache when buildings change
 */
export function clearPathfindingCache(): void {
  walkabilityCache.clear();
  walkabilityCacheVersion++;
}

/**
 * Check if a tile is walkable (land, not occupied by building, and no obstacle)
 */
export function isWalkable(tileX: number, tileZ: number): boolean {
  const key = `${tileX},${tileZ}`;

  const cached = walkabilityCache.get(key);
  if (cached !== undefined) return cached;

  // Check terrain - use tile center
  const worldX = tileX + 0.5;
  const worldZ = tileZ + 0.5;
  const h = tileHeight(worldX, worldZ);

  if (!isLand(h)) {
    walkabilityCache.set(key, false);
    return false;
  }

  // Check building occupancy
  if (tileOccupancy.isOccupied(tileX, tileZ)) {
    walkabilityCache.set(key, false);
    return false;
  }

  // Check decoration obstacles (trees, rocks, etc.)
  if (obstacleRegistry.hasObstacle(tileX, tileZ)) {
    walkabilityCache.set(key, false);
    return false;
  }

  walkabilityCache.set(key, true);
  return true;
}

/**
 * Heuristic function - Euclidean distance
 */
function heuristic(x1: number, z1: number, x2: number, z2: number): number {
  const dx = x2 - x1;
  const dz = z2 - z1;
  return Math.sqrt(dx * dx + dz * dz);
}

/**
 * Pack coordinates into a key for the closed set
 */
function packKey(x: number, z: number): number {
  return ((x + 32768) << 16) | ((z + 32768) & 0xFFFF);
}

/**
 * Find a path from start to goal using A*
 * Returns array of waypoints (world coordinates), or null if no path found
 */
export function findPath(
  startX: number,
  startZ: number,
  goalX: number,
  goalZ: number
): Waypoint[] | null {
  // Convert to tile coordinates
  const startTileX = Math.floor(startX);
  const startTileZ = Math.floor(startZ);
  const goalTileX = Math.floor(goalX);
  const goalTileZ = Math.floor(goalZ);

  // Quick check - if goal is not walkable, find nearest walkable tile
  let actualGoalX = goalTileX;
  let actualGoalZ = goalTileZ;

  if (!isWalkable(goalTileX, goalTileZ)) {
    // Search for nearest walkable tile around the goal
    let found = false;
    for (let radius = 1; radius <= 5 && !found; radius++) {
      for (let dx = -radius; dx <= radius && !found; dx++) {
        for (let dz = -radius; dz <= radius && !found; dz++) {
          if (Math.abs(dx) !== radius && Math.abs(dz) !== radius) continue;
          const checkX = goalTileX + dx;
          const checkZ = goalTileZ + dz;
          if (isWalkable(checkX, checkZ)) {
            actualGoalX = checkX;
            actualGoalZ = checkZ;
            found = true;
          }
        }
      }
    }
    if (!found) {
      // No walkable tile near goal
      return null;
    }
  }

  // If start equals goal, return empty path
  if (startTileX === actualGoalX && startTileZ === actualGoalZ) {
    return [{ x: goalX, z: goalZ }];
  }

  // A* algorithm
  const openSet = new MinHeap();
  const closedSet = new Set<number>();
  const nodeMap = new Map<number, PathNode>();

  const startNode: PathNode = {
    x: startTileX,
    z: startTileZ,
    g: 0,
    h: heuristic(startTileX, startTileZ, actualGoalX, actualGoalZ),
    f: 0,
    parent: null,
  };
  startNode.f = startNode.g + startNode.h;

  openSet.push(startNode);
  nodeMap.set(packKey(startTileX, startTileZ), startNode);

  let iterations = 0;

  while (!openSet.isEmpty() && iterations < MAX_ITERATIONS) {
    iterations++;

    const current = openSet.pop()!;
    const currentKey = packKey(current.x, current.z);

    // Skip if already processed
    if (closedSet.has(currentKey)) continue;
    closedSet.add(currentKey);

    // Goal reached
    if (current.x === actualGoalX && current.z === actualGoalZ) {
      return reconstructPath(current, goalX, goalZ);
    }

    // Explore neighbors
    for (const dir of DIRECTIONS) {
      const nx = current.x + dir.dx;
      const nz = current.z + dir.dz;
      const neighborKey = packKey(nx, nz);

      // Skip if already in closed set
      if (closedSet.has(neighborKey)) continue;

      // Skip if not walkable
      if (!isWalkable(nx, nz)) continue;

      // For diagonal movement, check that both adjacent tiles are walkable
      if (dir.dx !== 0 && dir.dz !== 0) {
        if (!isWalkable(current.x + dir.dx, current.z) ||
            !isWalkable(current.x, current.z + dir.dz)) {
          continue;
        }
      }

      const tentativeG = current.g + dir.cost;
      const existingNode = nodeMap.get(neighborKey);

      if (!existingNode || tentativeG < existingNode.g) {
        const h = heuristic(nx, nz, actualGoalX, actualGoalZ);
        const newNode: PathNode = {
          x: nx,
          z: nz,
          g: tentativeG,
          h: h,
          f: tentativeG + h,
          parent: current,
        };

        nodeMap.set(neighborKey, newNode);
        openSet.push(newNode);
      }
    }
  }

  // No path found - return direct path as fallback
  return null;
}

/**
 * Reconstruct path from goal node to start
 */
function reconstructPath(goalNode: PathNode, finalX: number, finalZ: number): Waypoint[] {
  const path: Waypoint[] = [];
  let current: PathNode | null = goalNode;

  // Build path in reverse
  while (current !== null) {
    path.push({
      x: current.x + 0.5, // Convert to world coordinates (tile center)
      z: current.z + 0.5,
    });
    current = current.parent;
  }

  // Reverse to get start-to-goal order
  path.reverse();

  // Replace last waypoint with exact goal position
  if (path.length > 0) {
    path[path.length - 1] = { x: finalX, z: finalZ };
  }

  // Simplify path by removing unnecessary waypoints
  return simplifyPath(path);
}

/**
 * Simplify path by removing waypoints that are in a straight line
 */
function simplifyPath(path: Waypoint[]): Waypoint[] {
  if (path.length <= 2) return path;

  const simplified: Waypoint[] = [path[0]];

  for (let i = 1; i < path.length - 1; i++) {
    const prev = simplified[simplified.length - 1];
    const curr = path[i];
    const next = path[i + 1];

    // Check if current point is on the line between prev and next
    const dx1 = curr.x - prev.x;
    const dz1 = curr.z - prev.z;
    const dx2 = next.x - curr.x;
    const dz2 = next.z - curr.z;

    // Normalize and compare directions
    const len1 = Math.sqrt(dx1 * dx1 + dz1 * dz1);
    const len2 = Math.sqrt(dx2 * dx2 + dz2 * dz2);

    if (len1 > 0.01 && len2 > 0.01) {
      const dot = (dx1 / len1) * (dx2 / len2) + (dz1 / len1) * (dz2 / len2);
      // If directions are nearly the same (dot product close to 1), skip this waypoint
      if (dot < 0.99) {
        simplified.push(curr);
      }
    }
  }

  simplified.push(path[path.length - 1]);
  return simplified;
}

/**
 * Check if there's a clear line of sight between two points
 * Uses Bresenham-like line algorithm
 */
export function hasLineOfSight(
  x1: number,
  z1: number,
  x2: number,
  z2: number
): boolean {
  const tileX1 = Math.floor(x1);
  const tileZ1 = Math.floor(z1);
  const tileX2 = Math.floor(x2);
  const tileZ2 = Math.floor(z2);

  const dx = Math.abs(tileX2 - tileX1);
  const dz = Math.abs(tileZ2 - tileZ1);
  const sx = tileX1 < tileX2 ? 1 : -1;
  const sz = tileZ1 < tileZ2 ? 1 : -1;

  let err = dx - dz;
  let x = tileX1;
  let z = tileZ1;

  while (true) {
    if (!isWalkable(x, z)) return false;

    if (x === tileX2 && z === tileZ2) break;

    const e2 = 2 * err;
    if (e2 > -dz) {
      err -= dz;
      x += sx;
    }
    if (e2 < dx) {
      err += dx;
      z += sz;
    }
  }

  return true;
}
